import { FC, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  EnumCutsheetAssignmentRuleSetRulesInspectionLevel,
  EnumCutsheetAssignmentRuleSetRulesSplitType,
  EnumUserType,
} from 'lib/graphql';

import { SearchableCustomer, useCustomerSearch } from './customer-search';

export interface LocalRule {
  uid: string;
  label?: string;
  inspectionLevel: EnumCutsheetAssignmentRuleSetRulesInspectionLevel[];
  splitType: EnumCutsheetAssignmentRuleSetRulesSplitType[];
  splitIndex: number[];
  producer: string[];
  buyer: string[];
  questionnaireId: string;
}

export const EMPTY_RULE: Omit<LocalRule, 'uid'> = {
  label: '',
  inspectionLevel: [],
  splitType: [],
  splitIndex: [],
  producer: [],
  buyer: [],
  questionnaireId: '',
};

export const SPLIT_INDEX_ELIGIBLE = new Set([
  EnumCutsheetAssignmentRuleSetRulesSplitType.Quarters,
  EnumCutsheetAssignmentRuleSetRulesSplitType.HalfAndTwoQuarters,
]);

export function splitIndexEligible(
  splitType: EnumCutsheetAssignmentRuleSetRulesSplitType[],
): boolean {
  return splitType.length === 1 && SPLIT_INDEX_ELIGIBLE.has(splitType[0]);
}

export const INSPECTION_LABELS: Record<
  EnumCutsheetAssignmentRuleSetRulesInspectionLevel,
  string
> = {
  [EnumCutsheetAssignmentRuleSetRulesInspectionLevel.Exempt]: 'Exempt',
  [EnumCutsheetAssignmentRuleSetRulesInspectionLevel.State]: 'State',
  [EnumCutsheetAssignmentRuleSetRulesInspectionLevel.Usda]: 'USDA',
};

export const SPLIT_LABELS: Record<
  EnumCutsheetAssignmentRuleSetRulesSplitType,
  string
> = {
  [EnumCutsheetAssignmentRuleSetRulesSplitType.Whole]: 'Whole',
  [EnumCutsheetAssignmentRuleSetRulesSplitType.Half]: 'Half',
  [EnumCutsheetAssignmentRuleSetRulesSplitType.Quarters]: 'Quarters',
  [EnumCutsheetAssignmentRuleSetRulesSplitType.HalfAndTwoQuarters]:
    'Half + Two Quarters',
};

function renderMultiChips<T extends string>(
  selected: T[],
  labels: Record<T, string>,
) {
  if (selected.length === 0)
    return (
      <Typography variant="caption" color="text.secondary">
        Any
      </Typography>
    );
  return (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {selected.map((v) => (
        <Chip key={v} label={labels[v]} size="small" />
      ))}
    </Box>
  );
}

export interface RuleDialogProps {
  open: boolean;
  initial: Omit<LocalRule, 'uid'> | null;
  questionnaires: { _id: string; name: string }[];
  vendorId?: string;
  onSave: (rule: Omit<LocalRule, 'uid'>) => void;
  onClose: () => void;
  title?: string;
  submitLabel?: string;
  lockQuestionnaire?: boolean;
  /** Optional body text shown above the form (e.g. "This cutsheet is a draft..."). */
  description?: string;
  /** When provided, renders a secondary action button (e.g. "Save as Draft"). */
  onSecondary?: () => void;
  secondaryLabel?: string;
  /**
   * When provided, renders a tertiary action button (e.g. "Publish without
   * Rule") that doesn't require the form to be valid.
   */
  onTertiary?: () => void;
  tertiaryLabel?: string;
}

export const RuleDialog: FC<RuleDialogProps> = ({
  open,
  initial,
  questionnaires,
  vendorId,
  onSave,
  onClose,
  title,
  submitLabel,
  lockQuestionnaire = false,
  description,
  onSecondary,
  secondaryLabel,
  onTertiary,
  tertiaryLabel,
}) => {
  const [form, setForm] = useState<Omit<LocalRule, 'uid'>>(
    initial ?? EMPTY_RULE,
  );
  const [producerSelected, setProducerSelected] = useState<
    SearchableCustomer[]
  >([]);
  const [buyerSelected, setBuyerSelected] = useState<SearchableCustomer[]>([]);

  const scopedProducerId =
    form.producer.length === 1 ? form.producer[0] : undefined;

  const producerSearch = useCustomerSearch({
    vendorId,
    relationshipType: EnumUserType.Producer,
    enabled: open,
  });
  const buyerSearch = useCustomerSearch({
    vendorId,
    relationshipType: EnumUserType.Buyer,
    producerId: scopedProducerId,
    enabled: open,
  });

  const handleEntered = () => {
    setForm(initial ?? EMPTY_RULE);
    setProducerSelected([]);
    setBuyerSelected([]);
    producerSearch.reset();
    buyerSearch.reset();
  };

  // Hydrate selected chips from search results once data arrives for IDs
  // present in form but not yet resolved to customer objects.
  useEffect(() => {
    if (!open) return;
    const needed = form.producer.filter(
      (id) => !producerSelected.some((s) => s._id === id),
    );
    if (needed.length === 0) return;
    const resolved = producerSearch.options.filter((o) =>
      needed.includes(o._id),
    );
    if (resolved.length > 0) {
      setProducerSelected((prev) => [...prev, ...resolved]);
    }
  }, [open, form.producer, producerSelected, producerSearch.options]);

  useEffect(() => {
    if (!open) return;
    const needed = form.buyer.filter(
      (id) => !buyerSelected.some((s) => s._id === id),
    );
    if (needed.length === 0) return;
    const resolved = buyerSearch.options.filter((o) => needed.includes(o._id));
    if (resolved.length > 0) {
      setBuyerSelected((prev) => [...prev, ...resolved]);
    }
  }, [open, form.buyer, buyerSelected, buyerSearch.options]);

  const handleSave = () => {
    if (!form.questionnaireId) return;
    onSave(form);
  };

  const handleMultiChange = <K extends 'inspectionLevel' | 'splitType'>(
    key: K,
    event: SelectChangeEvent<string[]>,
  ) => {
    const { value } = event.target;
    const parsed = (
      typeof value === 'string' ? value.split(',') : value
    ) as LocalRule[K];
    setForm((prev) => {
      const next = { ...prev, [key]: parsed };
      if (
        key === 'splitType' &&
        !splitIndexEligible(
          parsed as EnumCutsheetAssignmentRuleSetRulesSplitType[],
        )
      ) {
        next.splitIndex = [];
      }
      return next;
    });
  };

  const showSplitIndex = splitIndexEligible(form.splitType);

  const isEditing = !!initial?.questionnaireId;
  const resolvedTitle = title ?? (isEditing ? 'Edit Rule' : 'Add Rule');
  const resolvedSubmit =
    submitLabel ?? (isEditing ? 'Save Changes' : 'Add Rule');

  const buyerHelperText = scopedProducerId
    ? `Filtered to buyers for ${producerSelected[0]?.fullName ?? 'selected producer'}. Leave empty to match any buyer.`
    : 'Leave empty to match any buyer';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEntered: handleEntered }}
    >
      <DialogTitle>{resolvedTitle}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack spacing={2} direction="row">
            <TextField
              label="Label (optional)"
              value={form.label ?? ''}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, label: e.target.value }))
              }
              helperText="A short human-readable name (e.g. 'USDA Halves')"
              fullWidth
              size="small"
            />

            <FormControl
              fullWidth
              size="small"
              required
              disabled={lockQuestionnaire}
            >
              <InputLabel>Form Builder</InputLabel>
              <Select
                label="Form Builder"
                value={form.questionnaireId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    questionnaireId: e.target.value as string,
                  }))
                }
                error={!form.questionnaireId}
              >
                <MenuItem value="" disabled>
                  Select a Form Builder
                </MenuItem>
                {questionnaires.map((q) => (
                  <MenuItem key={q._id} value={q._id}>
                    {q.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <FormControl fullWidth size="small">
            <InputLabel>Inspection Level</InputLabel>
            <Select
              multiple
              label="Inspection Level"
              value={form.inspectionLevel}
              onChange={(e) =>
                handleMultiChange(
                  'inspectionLevel',
                  e as SelectChangeEvent<string[]>,
                )
              }
              input={<OutlinedInput label="Inspection Level" />}
              renderValue={(selected) =>
                renderMultiChips(
                  selected as EnumCutsheetAssignmentRuleSetRulesInspectionLevel[],
                  INSPECTION_LABELS,
                )
              }
            >
              {Object.values(
                EnumCutsheetAssignmentRuleSetRulesInspectionLevel,
              ).map((v) => (
                <MenuItem key={v} value={v}>
                  <Checkbox
                    checked={form.inspectionLevel.includes(v)}
                    size="small"
                  />
                  <ListItemText primary={INSPECTION_LABELS[v]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Split Type</InputLabel>
            <Select
              multiple
              label="Split Type"
              value={form.splitType}
              onChange={(e) =>
                handleMultiChange('splitType', e as SelectChangeEvent<string[]>)
              }
              input={<OutlinedInput label="Split Type" />}
              renderValue={(selected) =>
                renderMultiChips(
                  selected as EnumCutsheetAssignmentRuleSetRulesSplitType[],
                  SPLIT_LABELS,
                )
              }
            >
              {Object.values(EnumCutsheetAssignmentRuleSetRulesSplitType).map(
                (v) => (
                  <MenuItem key={v} value={v}>
                    <Checkbox
                      checked={form.splitType.includes(v)}
                      size="small"
                    />
                    <ListItemText primary={SPLIT_LABELS[v]} />
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>

          {showSplitIndex &&
            (() => {
              const quarterLabels =
                form.splitType[0] ===
                EnumCutsheetAssignmentRuleSetRulesSplitType.Quarters
                  ? ['Quarter 1', 'Quarter 2', 'Quarter 3', 'Quarter 4']
                  : ['Quarter 1', 'Quarter 2'];
              return (
                <FormControl fullWidth size="small">
                  <InputLabel>Quarters</InputLabel>
                  <Select
                    multiple
                    label="Quarters"
                    value={form.splitIndex}
                    onChange={(e) => {
                      const { value } = e.target;
                      setForm((prev) => ({
                        ...prev,
                        splitIndex: (typeof value === 'string'
                          ? value.split(',').map(Number)
                          : value) as number[],
                      }));
                    }}
                    input={<OutlinedInput label="Quarters" />}
                    renderValue={(selected) =>
                      (selected as number[]).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Any
                        </Typography>
                      ) : (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {(selected as number[]).map((i) => (
                            <Chip
                              key={i}
                              label={quarterLabels[i] ?? `Q${i + 1}`}
                              size="small"
                            />
                          ))}
                        </Box>
                      )
                    }
                  >
                    {quarterLabels.map((label, i) => (
                      <MenuItem key={i} value={i}>
                        <Checkbox
                          checked={form.splitIndex.includes(i)}
                          size="small"
                        />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            })()}

          <Autocomplete
            multiple
            options={producerSearch.options}
            loading={producerSearch.loading}
            filterOptions={(x) => x}
            getOptionLabel={(opt) => opt.fullName}
            value={producerSelected}
            inputValue={producerSearch.input}
            onInputChange={(_e, val, reason) => {
              if (reason === 'reset') return;
              producerSearch.onInputChange(val);
            }}
            onChange={(_e, val) => {
              setProducerSelected(val);
              setForm((prev) => ({
                ...prev,
                producer: val.map((v) => v._id),
              }));
            }}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Producers"
                size="small"
                helperText="Leave empty to match any producer"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {producerSearch.loading && (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            multiple
            options={buyerSearch.options}
            loading={buyerSearch.loading}
            filterOptions={(x) => x}
            getOptionLabel={(opt) => opt.fullName}
            value={buyerSelected}
            inputValue={buyerSearch.input}
            onInputChange={(_e, val, reason) => {
              if (reason === 'reset') return;
              buyerSearch.onInputChange(val);
            }}
            onChange={(_e, val) => {
              setBuyerSelected(val);
              setForm((prev) => ({
                ...prev,
                buyer: val.map((v) => v._id),
              }));
            }}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buyers"
                size="small"
                helperText={buyerHelperText}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {buyerSearch.loading && (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Alert severity="info" sx={{ fontSize: 12 }}>
            Multiple selections within a filter use OR logic (e.g. USDA{' '}
            <em>or</em> Exempt). Filters across dimensions use AND logic. Leave
            a dimension empty to match any value for it.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {onSecondary && (
          <Button onClick={onSecondary}>
            {secondaryLabel ?? 'Save as Draft'}
          </Button>
        )}
        {onTertiary && (
          <Button onClick={onTertiary} variant="outlined">
            {tertiaryLabel ?? 'Publish without Rule'}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!form.questionnaireId}
        >
          {resolvedSubmit}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
